import React, { FC, Fragment } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import isNil from 'lodash/isNil';
import moment from 'moment';

import { AddIcon, ChevronDownIcon, Select, SingleDatePicker } from 'src/shared/components';
import { CycleTypeEnums, PermissionsEnums, WeekDaysEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useDateTimeLabels } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getWeekDaysEnums, mapEnumKeyToArray } from 'src/shared/utils';

import { mapCycleTypeEnum } from './availabilities.utils';
import { EmployeeAvailabilityCycleInfoTable } from './EmployeeAvailabilityCycleInfoTable';
import { EmployeeAvailabilityCyclePreferencesTable } from './EmployeeAvailabilityCyclePreferencesTable';
import { useEmployeeAvailabilityAddCycle } from './providers/EmployeeAvailabilityAddCycleProvider';
import { useEmployeeAvailabilityFutureCycles } from './providers/EmployeeAvailabilityFutureCyclesProvider';

export const EmployeeAvailabilityFutureCyclesSection: FC = () => {
  const { t } = useTranslate();

  const {
    inputs: addCycleInputs,
    isAddMode,
    adding,
    apiError: addCycleApiError,
    touched: addCycleTouched,
    errors: addCycleErrors,
    onDayOfWeekChange,
    onStartDateChange: handleAddCycleStartDateChange,
    onAdd: handleAddCycle,
    onCancel: handleCancelAddCycle,
    onSave: handleSaveAddCycle,
  } = useEmployeeAvailabilityAddCycle();

  const {
    inputsList,
    loading,
    touched: futureCyclesTouched,
    errors: futureCyclesErrors,
    apiError: futureCyclesApiError,
    // Cycle
    updatingCycle,
    openCycle,
    editedCycle,
    onOpenCycleToggle,
    onEditedCycleChange,
    onStartDateChange: handleFutureCyclesStartDateChange,
    onCancelCycle,
    onSaveCycle,
    // Preferences
    updatingPreferences,
    editedCyclePeriodDate,
    onEditedCyclePeriodDateChange,
    onChangeFullDay,
    onChangeStatus,
    onDelete,
    onAdd: handleAddPreference,
    onChangeStartTime,
    onChangeEndTime,
    onCancelPreferences,
    onSavePreferences,
    onDeleteCycle,
    onDeleteDate,
  } = useEmployeeAvailabilityFutureCycles();

  const { mapWeekDaysEnumsToFullLabel } = useDateTimeLabels();

  if (!inputsList?.length && loading) {
    return <Skeleton className="rounded" variant="rectangular" animation="wave" height="72px" />;
  }

  return (
    <Stack direction="column">
      {!isAddMode && (
        <Stack direction="column" gap={2} pb={1}>
          {inputsList?.map((inputs, cycleIndex) => {
            const editedPeriodDate =
              editedCyclePeriodDate && editedCyclePeriodDate.cycleIndex === cycleIndex
                ? { periodKey: editedCyclePeriodDate.periodKey, dateKey: editedCyclePeriodDate.dateKey }
                : undefined;

            const isOpen = openCycle === inputs.id;

            return (
              <Fragment key={inputs.id}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => onOpenCycleToggle(inputs.id)}
                    className={`collapsible-label ${isOpen ? 'open' : ''}`}
                    disabled={!!editedCyclePeriodDate}
                  >
                    <ChevronDownIcon className="collapsible-chevron-icon" size={24} />
                  </IconButton>
                  <Typography fontSize={20} fontWeight={400} color="text.secondary">{`${t(
                    'availability.futureCycle',
                  )} ${cycleIndex + 1}`}</Typography>
                </Stack>

                {(editedCycle === cycleIndex || editedCyclePeriodDate?.cycleIndex === cycleIndex) &&
                  futureCyclesApiError && <Alert severity="error">{futureCyclesApiError}</Alert>}

                <EmployeeAvailabilityCycleInfoTable
                  cycle={inputs}
                  loading={loading}
                  updating={updatingCycle}
                  isEditMode={editedCycle === cycleIndex}
                  readOnly={!isNil(editedCycle) || !!editedCyclePeriodDate}
                  onEdit={() => onEditedCycleChange(cycleIndex)}
                  onStartDateChange={(date) => handleFutureCyclesStartDateChange(date, cycleIndex)}
                  onCancel={onCancelCycle}
                  onSave={onSaveCycle}
                  onDelete={() => onDeleteCycle(cycleIndex)}
                />

                <Collapse in={isOpen}>
                  <Stack direction="column" gap={2}>
                    <EmployeeAvailabilityCyclePreferencesTable
                      inputs={inputs}
                      touched={futureCyclesTouched[cycleIndex]}
                      errors={futureCyclesErrors[cycleIndex]}
                      readOnly={!isNil(editedCycle)}
                      loading={loading}
                      updating={updatingPreferences}
                      editedPeriodDate={editedPeriodDate}
                      onEditingChange={(periodKey, dateKey) =>
                        onEditedCyclePeriodDateChange(cycleIndex, periodKey, dateKey)
                      }
                      onCancel={onCancelPreferences}
                      onChangeFullDay={(wasAllDay, periodKey, dateKey) =>
                        onChangeFullDay(wasAllDay, cycleIndex, periodKey, dateKey)
                      }
                      onChangeStatus={(status, periodKey, dateKey, preferenceIndex) =>
                        onChangeStatus(status, cycleIndex, periodKey, dateKey, preferenceIndex)
                      }
                      onDelete={(periodKey, dateKey, preferenceIndex) =>
                        onDelete(cycleIndex, periodKey, dateKey, preferenceIndex)
                      }
                      onAdd={(periodKey, dateKey) => handleAddPreference(cycleIndex, periodKey, dateKey)}
                      onChangeStartTime={(value, periodKey, dateKey, preferenceIndex) =>
                        onChangeStartTime(value, cycleIndex, periodKey, dateKey, preferenceIndex)
                      }
                      onChangeEndTime={(value, periodKey, dateKey, preferenceIndex) =>
                        onChangeEndTime(value, cycleIndex, periodKey, dateKey, preferenceIndex)
                      }
                      onSave={onSavePreferences}
                      onDeleteDate={(periodKey, dateKey) => onDeleteDate(cycleIndex, periodKey, dateKey)}
                    />
                  </Stack>
                </Collapse>
              </Fragment>
            );
          })}

          <Divider />

          <ComponentWithPermissions permissions={PermissionsEnums.WORK_TIME_PREFERENCES_EDIT}>
            {!editedCyclePeriodDate && isNil(editedCycle) && (
              <Button
                sx={{ marginTop: 3, alignSelf: 'flex-start' }}
                size="small"
                startIcon={<AddIcon size={20} />}
                color="secondary"
                onClick={handleAddCycle}
              >
                {t('availability.addCycle')}
              </Button>
            )}
          </ComponentWithPermissions>
        </Stack>
      )}

      {isAddMode && (
        <>
          {addCycleApiError && <Alert severity="error">{addCycleApiError}</Alert>}
          <TableContainer sx={{ border: 'none', 'td, th': { border: 'none', p: 1 } }}>
            <Table padding="none" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {t('global.type')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {addCycleInputs.type === CycleTypeEnums.DAYS
                        ? t('global.length')
                        : t('availability.firstDayOfCycle')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ td: { verticalAlign: 'top' } }}>
                <TableRow>
                  <TableCell>
                    {/* Only weekly cycle is supported in ESS for add cycle */}
                    <Select value={addCycleInputs.type} className="w-100" disabled>
                      {mapEnumKeyToArray(CycleTypeEnums).map((cycleTypeKey) => (
                        <MenuItem
                          key={cycleTypeKey}
                          value={CycleTypeEnums[cycleTypeKey as keyof typeof CycleTypeEnums]}
                        >
                          {mapCycleTypeEnum(CycleTypeEnums[cycleTypeKey as keyof typeof CycleTypeEnums], t)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={addCycleInputs.dayOfWeek}
                      className="w-100"
                      onChange={(event) => onDayOfWeekChange(event.target.value)}
                      disabled={adding}
                    >
                      {getWeekDaysEnums().map((weekDay) => (
                        <MenuItem key={weekDay} value={WeekDaysEnums[weekDay]}>
                          {mapWeekDaysEnumsToFullLabel[WeekDaysEnums[weekDay]]}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer sx={{ border: 'none', 'td, th': { border: 'none', p: 1 } }}>
            <Table padding="none" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {t('availability.startDate')}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody sx={{ td: { verticalAlign: 'top' } }}>
                <TableRow>
                  <TableCell>
                    <SingleDatePicker
                      minDate={moment()}
                      value={addCycleInputs.validFrom ? moment(addCycleInputs.validFrom) : null}
                      onChange={handleAddCycleStartDateChange}
                      placeholder={t('absences.selectDate')}
                      disabled={adding}
                      error={addCycleTouched ? addCycleErrors.validFrom : undefined}
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" alignItems="center" gap={2} paddingLeft={1} marginTop={2}>
            <Button color="secondary" onClick={handleCancelAddCycle}>
              {t('global.cancel')}
            </Button>
            <LoadingButton variant="contained" onClick={handleSaveAddCycle} loading={adding}>
              {t('global.save')}
            </LoadingButton>
          </Stack>
        </>
      )}
    </Stack>
  );
};
